import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, Layers3, PieChart } from "lucide-react";

export default function Allocation() {
  const [alloc, setAlloc] = useState(null);
  const [kelly, setKelly] = useState(null);
  const [symbol, setSymbol] = useState("EURUSD");
  const [loading, setLoading] = useState(false);

  // Kelly multi form
  const [assets, setAssets] = useState([
    { symbol: "EURUSD", win_rate: 0.58, avg_win: 1.5, avg_loss: 1.0 },
    { symbol: "GBPUSD", win_rate: 0.55, avg_win: 1.4, avg_loss: 1.0 },
    { symbol: "XAUUSD", win_rate: 0.62, avg_win: 1.8, avg_loss: 1.0 },
    { symbol: "BTCUSD", win_rate: 0.53, avg_win: 2.2, avg_loss: 1.0 },
  ]);
  const [fraction, setFraction] = useState(0.25);

  const loadAlloc = async () => {
    setLoading(true);
    try {
      const r = await api.regimeAllocation(symbol, "H1");
      setAlloc(r);
    } catch (e) { toast.error("Erreur allocation"); }
    finally { setLoading(false); }
  };

  const runKelly = async () => {
    try {
      const r = await api.kellyMulti(assets, {}, fraction);
      setKelly(r);
    } catch (e) { toast.error("Erreur Kelly"); }
  };

  useEffect(() => { loadAlloc(); }, [symbol]);

  return (
    <div className="p-6 space-y-6" data-testid="allocation-page">
      <div className="flex justify-between items-end">
        <div>
          <h1 className="font-heading font-bold text-2xl tracking-tight">Allocation & Kelly Multi-Actifs</h1>
          <p className="text-white/50 text-sm mt-1">Allocation régime-switching + Kelly fractionnaire avec pénalité de corrélation.</p>
        </div>
      </div>

      {/* Regime allocation */}
      <div className="panel p-5">
        <div className="flex justify-between mb-4 items-end">
          <div className="panel-title"><Layers3 size={14} className="inline mr-2" />Allocation par régime détecté</div>
          <div className="flex gap-2 items-end">
            <div>
              <label className="label">Symbole ref.</label>
              <select className="field" value={symbol} onChange={(e) => setSymbol(e.target.value)} data-testid="alloc-symbol">
                {["EURUSD", "GBPUSD", "XAUUSD", "BTCUSD", "NAS100", "SPX500"].map((s) => <option key={s}>{s}</option>)}
              </select>
            </div>
            <button className="btn" onClick={loadAlloc} disabled={loading} data-testid="alloc-refresh">
              {loading ? <Loader2 size={14} className="animate-spin" /> : "Actualiser"}
            </button>
          </div>
        </div>
        {alloc ? (
          <div className="space-y-4">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <div className="p-4 bg-accent-blue/10 border border-accent-blue/30 rounded">
                <div className="text-xs uppercase tracking-widest text-white/50">Régime détecté</div>
                <div className="font-heading text-xl mt-2 font-bold text-accent-blue" data-testid="detected-regime">{alloc.regime}</div>
              </div>
              <div className="p-4 bg-white/5 rounded">
                <div className="text-xs uppercase tracking-widest text-white/50">Exposition cible brute</div>
                <div className="font-mono text-xl mt-2 font-bold">{alloc.target_gross_exposure_pct}%</div>
              </div>
              <div className="p-4 bg-white/5 rounded">
                <div className="text-xs uppercase tracking-widest text-white/50">Kelly fraction</div>
                <div className="font-mono text-xl mt-2 font-bold">{(alloc.kelly_fraction * 100).toFixed(0)}%</div>
              </div>
              <div className="p-4 bg-white/5 rounded">
                <div className="text-xs uppercase tracking-widest text-white/50">Notional / symbole</div>
                <div className="font-mono text-xl mt-2 font-bold">${alloc.notional_per_symbol?.toLocaleString()}</div>
              </div>
            </div>

            <div className="p-3 bg-white/5 rounded">
              <div className="text-xs text-white/50 mb-2">Répartition des stratégies</div>
              <div className="flex gap-2 h-3 rounded overflow-hidden">
                <div className="bg-accent-blue" style={{ width: `${alloc.strategy_weights.trend * 100}%` }} title={`Trend: ${(alloc.strategy_weights.trend * 100).toFixed(0)}%`} />
                <div className="bg-warn" style={{ width: `${alloc.strategy_weights.reversion * 100}%` }} title={`Reversion: ${(alloc.strategy_weights.reversion * 100).toFixed(0)}%`} />
                <div className="bg-up" style={{ width: `${alloc.strategy_weights.carry * 100}%` }} title={`Carry: ${(alloc.strategy_weights.carry * 100).toFixed(0)}%`} />
              </div>
              <div className="flex justify-between text-[10px] text-white/50 mt-1">
                <span>Trend {Math.round(alloc.strategy_weights.trend * 100)}%</span>
                <span>Reversion {Math.round(alloc.strategy_weights.reversion * 100)}%</span>
                <span>Carry {Math.round(alloc.strategy_weights.carry * 100)}%</span>
              </div>
            </div>
            <div className="text-sm text-white/70">{alloc.recommendation}</div>
          </div>
        ) : <div className="text-white/40">Chargement…</div>}
      </div>

      {/* Kelly multi */}
      <div className="panel p-5">
        <div className="panel-title mb-4"><PieChart size={14} className="inline mr-2" />Kelly fractionnaire multi-actifs</div>
        <div className="mb-4">
          <label className="label">Fraction Kelly (25% recommandé)</label>
          <input type="range" min="0.1" max="0.5" step="0.05" value={fraction} onChange={(e) => setFraction(parseFloat(e.target.value))} className="w-full" data-testid="kelly-fraction" />
          <div className="text-sm text-white/60 mt-1">{(fraction * 100).toFixed(0)}%</div>
        </div>

        <table className="w-full text-sm" data-testid="kelly-inputs">
          <thead><tr className="text-white/50 text-xs">
            <th className="text-left py-2">Symbole</th>
            <th className="text-right">Win Rate</th>
            <th className="text-right">Avg Win</th>
            <th className="text-right">Avg Loss</th>
          </tr></thead>
          <tbody>
            {assets.map((a, i) => (
              <tr key={i} className="border-t border-white/5">
                <td className="py-2 font-mono">
                  <input className="field w-24" value={a.symbol} onChange={(e) => {
                    const c = [...assets]; c[i].symbol = e.target.value; setAssets(c);
                  }} />
                </td>
                <td className="text-right"><input type="number" step="0.01" className="field w-20 text-right" value={a.win_rate} onChange={(e) => { const c = [...assets]; c[i].win_rate = parseFloat(e.target.value); setAssets(c); }} /></td>
                <td className="text-right"><input type="number" step="0.1" className="field w-20 text-right" value={a.avg_win} onChange={(e) => { const c = [...assets]; c[i].avg_win = parseFloat(e.target.value); setAssets(c); }} /></td>
                <td className="text-right"><input type="number" step="0.1" className="field w-20 text-right" value={a.avg_loss} onChange={(e) => { const c = [...assets]; c[i].avg_loss = parseFloat(e.target.value); setAssets(c); }} /></td>
              </tr>
            ))}
          </tbody>
        </table>
        <button className="btn btn-primary mt-3" onClick={runKelly} data-testid="run-kelly-btn">Calculer Kelly optimal</button>

        {kelly?.ok && (
          <div className="mt-4 p-3 bg-white/5 rounded" data-testid="kelly-output">
            <div className="text-xs text-white/50 uppercase mb-2">Allocation optimale (corrélation ajustée)</div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
              {Object.entries(kelly.correlation_adjusted_weights_pct).map(([sym, pct]) => (
                <div key={sym} className="p-2 bg-white/5 rounded flex justify-between">
                  <span className="font-mono text-sm">{sym}</span>
                  <span className="font-mono font-bold text-accent-blue">{pct}%</span>
                </div>
              ))}
            </div>
            <div className="mt-3 text-sm">Total : <b>{kelly.total_allocation_pct}%</b> de l'equity</div>
            <ul className="text-xs text-white/50 list-disc list-inside mt-2 space-y-1">
              {kelly.notes.map((n, i) => <li key={i}>{n}</li>)}
            </ul>
          </div>
        )}
      </div>
    </div>
  );
}
